import type { AgentThreadListEntry } from '@reflex/ui/components/agent/AgentThreadList';
import { parseEventPayload } from '@reflex/ui/client/event-utils';
import type { AxonEvent } from '@/lib/types';

/**
 * Shared model of `agent.subagent` stream events: how a `started` event pairs
 * with its terminal sibling, and how the raw provider agent types map onto
 * display labels. Consumed by the transcript's `AgentSubagentBubble` and by
 * the composer's activity bar so both read the same launches the same way.
 */

export type SubagentStatus = 'started' | 'completed' | 'failed' | 'stopped';
export type TerminalSubagentStatus = Exclude<SubagentStatus, 'started'>;

export interface SubagentPayload {
  subagentType?: string;
  description?: string;
  status?: SubagentStatus;
  taskId?: string;
  toolUseId?: string;
}

interface ClaudeTaskSystemPayload {
  subtype?: string;
  task_id?: string;
  task_type?: string;
  status?: TerminalSubagentStatus;
  patch?: {
    status?: TerminalSubagentStatus;
  };
}

/** Normalize Claude Code's PascalCase agent types to our lowercase keys */
export function normalizeSubagentType(type: string): string {
  const lower = type.toLowerCase();
  if (lower === 'explore') return 'explore';
  if (lower === 'bash' || lower === 'shell' || lower === 'local_bash') return 'bash';
  if (lower === 'plan') return 'plan';
  if (lower === 'generalpurpose' || lower === 'local_agent') return 'generalPurpose';
  return type;
}

export function subagentLabel(type: string): string {
  const normalized = normalizeSubagentType(type);
  switch (normalized) {
    case 'explore':
      return 'Exploring';
    case 'bash':
      return 'Shell';
    case 'generalPurpose':
      return 'Agent';
    case 'plan':
      return 'Planning';
    default:
      return type;
  }
}

export function isTerminalSubagentStatus(status: unknown): status is TerminalSubagentStatus {
  return status === 'completed' || status === 'failed' || status === 'stopped';
}

/**
 * Does `candidate` close the launch described by `started`? A terminal
 * `agent.subagent` event matches by `taskId` (or by normalized type when the
 * launch carried none), and Claude's `system` task updates match by `task_id`.
 */
export function matchCompletion(
  candidate: AxonEvent,
  started: { taskId?: string; normalizedType: string },
): { event: AxonEvent; status: TerminalSubagentStatus } | null {
  const subagentPayload = parseEventPayload<SubagentPayload>(candidate.payload);
  if (candidate.type === 'agent.subagent') {
    const matchesTask = started.taskId && subagentPayload?.taskId === started.taskId;
    const matchesType =
      !started.taskId &&
      normalizeSubagentType(subagentPayload?.subagentType ?? '') === started.normalizedType;

    if ((matchesTask || matchesType) && isTerminalSubagentStatus(subagentPayload?.status)) {
      return { event: candidate, status: subagentPayload.status };
    }
  }

  if (candidate.type !== 'system' || !started.taskId) return null;

  const systemPayload = parseEventPayload<ClaudeTaskSystemPayload>(candidate.payload);
  if (
    systemPayload?.task_id !== started.taskId ||
    (systemPayload.subtype !== 'task_updated' && systemPayload.subtype !== 'task_notification')
  ) {
    return null;
  }

  const status = systemPayload.patch?.status ?? systemPayload.status;
  if (!isTerminalSubagentStatus(status)) return null;

  return { event: candidate, status };
}

const ENTRY_MARK: Record<TerminalSubagentStatus, AgentThreadListEntry['mark']> = {
  completed: 'completed',
  failed: 'failed',
  stopped: undefined,
};

interface OpenLaunch {
  entry: AgentThreadListEntry;
  taskId?: string;
  normalizedType: string;
  hasDescription: boolean;
}

function closeLaunch(open: OpenLaunch[], index: number, status: TerminalSubagentStatus): void {
  const [launch] = open.splice(index, 1);
  launch.entry.turnState = 'idle';
  launch.entry.mark = ENTRY_MARK[status];
  if (!launch.hasDescription) launch.entry.headline = 'Finished';
}

/**
 * Fold a stream into the thread's sub-agent launches, newest first, in the
 * shape the shared `AgentThreadList` row renders. Each `started` event
 * becomes one entry; the first matching terminal event moves it to rest and
 * stamps the outcome mark. A single pass with an open-launch list keeps this
 * O(n) on the hot stream-render path, and consuming each terminal event
 * means two concurrent same-type launches without task ids close one at a
 * time instead of both dying to the first `completed`.
 */
export function deriveSubagentEntries(events: AxonEvent[]): AgentThreadListEntry[] {
  const entries: AgentThreadListEntry[] = [];
  const open: OpenLaunch[] = [];

  for (const event of events) {
    if (event.type === 'agent.subagent') {
      const payload = parseEventPayload<SubagentPayload>(event.payload);
      if (!payload?.subagentType) continue;
      const normalizedType = normalizeSubagentType(payload.subagentType);

      if (payload.status === 'started') {
        const entry: AgentThreadListEntry = {
          id: event.id,
          name: subagentLabel(payload.subagentType),
          turnState: 'working',
          headline: payload.description || 'Working',
        };
        entries.push(entry);
        open.push({
          entry,
          taskId: payload.taskId,
          normalizedType,
          hasDescription: !!payload.description,
        });
        continue;
      }

      if (isTerminalSubagentStatus(payload.status)) {
        const index = open.findIndex((launch) =>
          launch.taskId
            ? payload.taskId === launch.taskId
            : launch.normalizedType === normalizedType,
        );
        if (index !== -1) closeLaunch(open, index, payload.status);
      }
      continue;
    }

    if (event.type === 'system') {
      const payload = parseEventPayload<ClaudeTaskSystemPayload>(event.payload);
      if (
        !payload?.task_id ||
        (payload.subtype !== 'task_updated' && payload.subtype !== 'task_notification')
      ) {
        continue;
      }
      const status = payload.patch?.status ?? payload.status;
      if (!isTerminalSubagentStatus(status)) continue;
      const index = open.findIndex((launch) => launch.taskId === payload.task_id);
      if (index !== -1) closeLaunch(open, index, status);
    }
  }
  return entries.reverse();
}
